// Crear una ocasión que no está en la lista.
//
// POR QUÉ PIDE DOS COSAS Y NO UNA
//
// Un nombre solo no alcanza, y acá la razón es más fuerte que con los
// colores.
//
// La FORMALIDAD no es un campo de la prenda: se deduce de las ocasiones
// que tiene marcadas. Algo etiquetado solo para Gym no es lo mismo que
// algo etiquetado para Trabajo día especial, y de esa diferencia sale la
// regla que impide combinar la remera del gym con el pantalón de vestir.
//
// Una ocasión sin nivel no sería una etiqueta incompleta: haría que la
// prenda marcada solo con ella NO TENGA rango de formalidad, y una
// prenda sin rango SE SALTEA LA REGLA ENTERA. En silencio. Combinaría
// con cualquier cosa y nadie entendería por qué.
//
// CÓMO SE PREGUNTA
//
// No con un número. Pedirle a alguien "elegí del 0 al 6" es pedirle que
// adivine una escala que no vio nunca. Se pregunta con ejemplos de la
// lista base —"¿es como Trabajo o como Casual?"— que es algo que se
// puede contestar mirando el propio placard.

import { useState } from "react";
import * as api from "./api";
import { invalidarOpciones } from "./opciones";
import { t, tCat, tError, useIdioma } from "./i18n";

export default function NuevaOcasion({ niveles, onCreada, onCerrar }) {
  useIdioma();
  const [nombre, setNombre] = useState("");
  // Arranca en 2 ("Salir a la calle"): es el nivel más frecuente y el
  // menos dañino si alguien no presta atención — está en el medio, así
  // que el error máximo es de tres escalones en vez de seis.
  const [nivel, setNivel] = useState(2);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  async function crear() {
    if (!nombre.trim()) return;

    setGuardando(true);
    setError("");
    try {
      const ocasion = await api.crearOcasion({
        nombre: nombre.trim(),
        nivel,
      });
      // Tirar el caché de opciones: si no, la ocasión recién creada no
      // aparece en los filtros de las otras pantallas hasta recargar la
      // página, y el usuario concluye que no se guardó.
      invalidarOpciones();
      setNombre("");
      onCreada?.(ocasion);
    } catch (e2) {
      setError(tError(e2));
    } finally {
      setGuardando(false);
    }
  }

  /* ⚠️  ESTO NO ES UN <form>, Y NO PUEDE SERLO (2026-08-21).
   *
   * EL BUG QUE COSTÓ SEIS VUELTAS
   *
   * Este componente se renderiza DENTRO del <form> de EditarPrenda. HTML
   * PROHÍBE los formularios anidados: el parser del navegador descarta el
   * <form> interno y se queda solo con su contenido.
   *
   * El resultado no es un error — es algo peor:
   *
   *   - El `onSubmit` de acá NUNCA se dispara.
   *   - El <button type="submit"> pasa a ser el submit del formulario DE
   *     AFUERA.
   *   - Tocar "Crear" GUARDA LA PRENDA y cierra el editor.
   *
   * Desde afuera se ve idéntico a que funcionó: el panel se cierra, no
   * hay cartel rojo, y no se creó nada. El dueño reportó seis veces que
   * su ocasión "no aparecía" — nunca había llegado a existir.
   *
   * Y explica por qué los colores propios tampoco andaban desde el primer
   * día: este archivo se copió de NuevoColor, que tenía el mismo <form>.
   *
   * LA REGLA: un componente que puede vivir adentro de otro no declara un
   * <form>. Usa un <div> y un botón normal con onClick. El costo es tener
   * que manejar Enter a mano; el beneficio es que funciona.
   */
  return (
    <div className="nuevo-color">
      {error && <div className="mensaje-error texto-mini" role="alert">{error}</div>}

      <label className="campo-mini">
        <span>{t("ocasiones.nombre", "Nombre de la ocasión")}</span>
        <input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t("ocasiones.ejemplos", "Misa, Asado, Viaje largo...")}
          maxLength={30}
          autoFocus
          // Enter para crear. Lo daba gratis el <form>; sin él hay que
          // ponerlo a mano, y vale la pena: escribir un nombre y apretar
          // Enter es lo que hace cualquiera.
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              crear();
            }
          }}
        />
      </label>

      <div className="campo-mini">
        <span>{t("ocasiones.queTanFormal", "¿Qué tan formal es? (para que combine bien)")}</span>
        <div className="niveles">
          {(niveles || []).map((n) => (
            <button
              key={n.nivel}
              type="button"
              className={`nivel-op ${nivel === n.nivel ? "nivel-activo" : ""}`}
              onClick={() => setNivel(n.nivel)}
            >
              <strong>{t(`opciones.nivel.${n.nivel}`, n.etiqueta)}</strong>
              <span className="tenue texto-mini">
                {n.ejemplo &&
                  t("ocasiones.como", "como {ocasion}", {
                    ocasion: tCat("ocasion", n.ejemplo),
                  })}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Se dice qué hace el dato, no que "es obligatorio". La
          diferencia importa: alguien que entiende para qué sirve elige
          bien; alguien que solo sabe que es obligatorio toca el primero
          y sigue. */}
      <p className="texto-mini tenue" style={{ margin: 0 }}>
        {t("ocasiones.paraQue", "Con esto la app sabe qué prendas pueden ir juntas. Una ocasión de oficina no se mezcla con ropa de gimnasio.")}
      </p>

      <div className="editor-acciones">
        <button
          className="boton-secundario"
          type="button"
          onClick={crear}
          disabled={guardando}
        >
          {guardando
            ? t("ocasiones.creando", "Creando...")
            : t("ocasiones.crear", "Crear ocasión")}
        </button>
        <button className="boton-texto" type="button" onClick={onCerrar}>
          {t("ocasiones.cancelar", "Cancelar")}
        </button>
      </div>
    </div>
  );
}
